import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api } from "../../lib/api";
import { AdminGate } from "../events/AdminGate";
import { fmtNum, fmtQty } from "./kitchenUtils";

const Box = () => <span className="inline-block h-4 w-4 border-2 border-black shrink-0 mt-0.5" />;
const unitWord = (ln) => (ln.pan_label ? `${ln.pan_label}${ln.quantity === 1 || ln.pan_label === "person" ? "" : "s"}` : "×");

const Field = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-black/55">{label}</p>
    <div className="text-[12.5px] leading-snug">{children || "—"}</div>
  </div>
);

const Row = ({ text, amount, testid }) => (
  <li className="flex items-start gap-2 py-1 border-b border-dashed border-black/20" data-testid={testid}>
    <Box /><span className="flex-1">{text}</span><span className="font-bold tabular-nums">{amount}</span>
  </li>
);

const ItemBlock = ({ ln, i }) => (
  <section className="break-inside-avoid border border-black/40 rounded mb-3" data-testid={`ticket-item-${i}`}>
    <div className="flex items-start gap-2 bg-black/5 px-3 py-2">
      <Box />
      <div className="flex-1">
        <p className="font-black text-[14px]">{fmtNum(ln.quantity)} {unitWord(ln)} — {ln.name}{ln.servings ? <span className="font-normal text-[12px]"> · serves ~{fmtNum(ln.servings)}</span> : null}</p>
        {ln.options && <p className="text-[12px]"><b>Options:</b> {ln.options}</p>}
        {ln.portion_note && <p className="text-[11.5px]">Portion: {ln.portion_note}</p>}
      </div>
    </div>
    <ul className="px-3 py-1 text-[12.5px]">
      {ln.components.map((c, j) => (
        <Row key={`c${j}`} testid={`ticket-item-${i}-component-${j}`} text={c.name}
          amount={c.total != null ? `${fmtNum(c.total)} ${c.unit_label}` : "amount: ______"} />
      ))}
      {ln.ingredients.map((g) => (
        <Row key={g.ingredient_id} testid={`ticket-item-${i}-ingredient-${g.ingredient_id}`} text={g.name}
          amount={`${fmtQty(g.total, g.unit, g.count_label)}${g.cut_into ? ` → ${fmtNum(g.cut_total)} ${g.cut_label}` : ""}`} />
      ))}
      {!ln.components.length && !ln.ingredients.length && (
        <li className="py-1 italic text-[11.5px]">No recipe yet — add one in Kitchen → Recipes (it's on the missing-info worksheet).</li>
      )}
    </ul>
  </section>
);

const Header = ({ o }) => (
  <>
    <div className="flex items-start justify-between border-b-2 border-black pb-2">
      <div>
        <p className="text-[22px] font-black leading-none">KITCHEN TICKET</p>
        <p className="text-[12px] mt-1">Fresh Fast Foods LLC · printed {new Date().toLocaleString()}</p>
      </div>
      <div className="text-right text-[12.5px]">
        <p className="font-black text-[15px]" data-testid="ticket-number">{o.invoice_number || o.order_number}</p>
        <p>Event: <b>{o.event_date || "—"}</b></p>
        <p>{o.fulfillment === "pickup" ? "Pickup" : "Delivery"} time: <b>{o.event_time || "—"}</b></p>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 py-3 border-b border-black/30">
      <Field label="Customer">{o.organization || o.customer_name}{o.organization && o.customer_name ? <><br />{o.customer_name}</> : null}{o.customer_phone ? <><br />{o.customer_phone}</> : null}</Field>
      <Field label={o.fulfillment === "pickup" ? "Pickup" : "Ship to"}>
        {o.fulfillment === "pickup" ? "Customer pickup" : <>{o.address}{o.address_line2 ? <><br />{o.address_line2}</> : null}<br />{[o.city, o.state, o.zip_code].filter(Boolean).join(" ")}</>}
      </Field>
      <Field label="On-site contact">
        {o.on_site_contact_name}{o.on_site_contact_phone ? <><br />{o.on_site_contact_phone}</> : null}
        {(o.has_receiving_personnel || o.has_delivery_cart) && <><br />{[o.has_receiving_personnel && "Staff on-site", o.has_delivery_cart && "Cart available"].filter(Boolean).join(" · ")}</>}
      </Field>
    </div>
  </>
);

const Instructions = ({ o }) => {
  const prep = o.lines.filter((l) => l.prep_notes);
  return (
    <section className="break-inside-avoid border-2 border-black rounded p-3 mt-4" data-testid="ticket-instructions">
      <p className="font-black text-[13px] uppercase tracking-[0.1em] mb-1">Instructions</p>
      <p className="text-[12.5px]"><b>Delivery / access notes:</b> {o.delivery_instructions || "N/A"}</p>
      <p className="text-[12.5px]"><b>Customer notes:</b> {o.notes || "N/A"}</p>
      {prep.map((l, i) => <p key={i} className="text-[12.5px]"><b>{l.name}:</b> {l.prep_notes}</p>)}
      <div className="grid grid-cols-2 gap-6 mt-4 text-[12px]">
        <p>Packed by: ____________________</p><p>Checked by: ____________________</p>
      </div>
    </section>
  );
};

function TicketInner() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const printed = useRef(false);
  useEffect(() => {
    api.get(`/admin/kitchen/prep/order/${orderId}`).then((r) => setO(r.data.order)).catch(() => setO({ error: true }));
  }, [orderId]);
  useEffect(() => {
    if (!o || o.error || printed.current) return undefined;
    const t = setTimeout(() => { printed.current = true; window.print(); }, 500);
    return () => clearTimeout(t);
  }, [o]);
  if (!o) return <p className="p-6">Loading…</p>;
  if (o.error) return <p className="p-6" data-testid="ticket-error">Couldn't load this order. Check your connection and reload.</p>;
  return (
    <div className="bg-white text-black p-6 max-w-[800px] mx-auto" data-testid="kitchen-ticket">
      <div className="flex justify-end print:hidden mb-3">
        <button type="button" onClick={() => window.print()} data-testid="ticket-print-button" className="h-10 px-4 rounded-full bg-black text-white text-[12px] font-bold inline-flex items-center gap-2"><Printer className="h-4 w-4" /> Print</button>
      </div>
      <Header o={o} />
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] mt-3 mb-2">Items to pack ({o.lines.length})</p>
      {o.lines.map((ln, i) => <ItemBlock key={i} ln={ln} i={i} />)}
      <Instructions o={o} />
    </div>
  );
}

export default function KitchenTicketPage() {
  return <AdminGate><TicketInner /></AdminGate>;
}
